import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { getAllWalletTransactionUserWise } from "./services/adminWalletUser.services";
import ShadowCard from "../../components/common/ShadowCard";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import {
  GridCustomCellProps,
  GridCustomHeaderCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  Grid as KendoGrid,
  GridColumn as Column,
} from "@progress/kendo-react-grid";
import {
  HeaderThElement,
  PagerTargetEvent,
} from "@progress/kendo-react-data-tools";
import { formatIndianNumber } from "../../_helper/helper";
import { clearWalletTransactionDetails } from "./adminWalletUserSlice";

const AdminWalletTransactionView = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const gridRef = useRef<any>(null);
  const location = useLocation();
  const UserID = location?.state?.UserID;

  const loading = useAppSelector((state) => state.adminWalletUser.loading);
  const WalletTransactionDetails = useAppSelector(
    (state) => state.adminWalletUser.WalletTransactionDetails
  );

  useEffect(() => {
    const payload = {
      UserID: UserID ? +UserID : null,
      Limit: 500,
      Skip: 0,
    };
    dispatch(getAllWalletTransactionUserWise(payload));

    return () => {
      dispatch(clearWalletTransactionDetails());
    };
  }, []);

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div className="container-fluid py-4">
        <div className="d-flex align-items-center mb-4">
          <button
            onClick={() => navigate(-1)}
            className="btn btn-link me-3"
            style={{
              color: "#00a34d",
              background: "white",
              padding: "4px 6px 6px",
            }}
          >
            <FaArrowLeft size={20} />
          </button>
          <h2 className="mb-0">Admin Wallet Transactions</h2>
        </div>
        <ShadowCard style={{ padding: 10, boxShadow: "none" }}>
          <div className="row">
            <div className="col-12 ">
              <table className="w-100">
                <tr>
                  <th className="p-1">User Name</th>
                  <th className="p-1">:</th>
                  <td className="p-1" colSpan={3}>
                    {WalletTransactionDetails?.first_name}{" "}
                    {WalletTransactionDetails?.last_name}
                  </td>
                </tr>
                <tr>
                  <th className="p-1" style={{ width: "15%" }}>
                    Mobile No.
                  </th>
                  <th className="p-1" style={{ width: "5%" }}>
                    :
                  </th>
                  <td className="p-1" style={{ width: "30%" }}>
                    {WalletTransactionDetails?.mobile_no}
                  </td>
                  <th className="p-1" style={{ width: "15%" }}>
                    Email ID
                  </th>
                  <th className="p-1" style={{ width: "5%" }}>
                    :
                  </th>
                  <td className="p-1" style={{ width: "30%" }}>
                    {WalletTransactionDetails?.email_id}
                  </td>
                </tr>
                <tr>
                  <th className="p-1">Current Bal.</th>
                  <th className="p-1">:</th>
                  <td className="p-1 fw-600 text-success" colSpan={3}>
                    {WalletTransactionDetails?.wallet_balance}
                  </td>
                </tr>
              </table>
            </div>
            <div className="col-12 ">
              <TransactionGridComponent
                WalletTransactions={
                  WalletTransactionDetails?.WalletTransactions || []
                }
              />
            </div>
          </div>
        </ShadowCard>
      </div>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const TransactionGridComponent = ({
  WalletTransactions,
}: {
  WalletTransactions: any[];
}) => {
  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All"
        ? WalletTransactions?.length
        : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className="table-header"
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  return (
    <>
      {/* {WalletTransactions && WalletTransactions.length > 0 ? ( */}
      <div className="mt-3">
        <KendoGrid
          className="responsive-table"
          style={{ maxHeight: "calc(100vh - 152px)" }}
          filterable={true}
          filter={filter}
          data={
            filter
              ? filterBy(WalletTransactions, filter).slice(
                  page.skip,
                  page.take + page.skip
                )
              : WalletTransactions.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={WalletTransactions.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="ID"
            title="Transaction ID"
            width={130}
            cells={{ data: (props: any) => <td>{props.dataItem?.ID}</td> }}
          />
          <Column field="TransactionDate" title="Date & Time" width={150} />
          <Column
            field="TransactionAmount"
            title="Amount"
            width={150}
            cells={{
              data: (props: any) => (
                <td
                  className="text-end fw-600"
                  style={{
                    color: props.dataItem.TransactionType
                      ? props.dataItem.TransactionType === "DEBIT"
                        ? "red"
                        : props.dataItem.TransactionType === "CREDIT"
                        ? "green"
                        : "#000"
                      : "#000",
                  }}
                >
                  {props.dataItem?.TransactionAmount
                    ? `${
                        props.dataItem.TransactionType &&
                        props.dataItem.TransactionType === "DEBIT"
                          ? "-"
                          : ""
                      }${formatIndianNumber(props.dataItem?.TransactionAmount)}`
                    : ""}
                </td>
              ),
            }}
          />
          <Column field="TransactionRemarks" title="Details" width={300} />
          <Column
            field="Balance"
            title="Balance"
            width={150}
            filterable={false}
            cells={{
              data: (props: any) => (
                <td
                  className="text-end fw-600"
                  // style={{
                  //   color: props.dataItem.TransactionType
                  //     ? props.dataItem.TransactionType === "DEBIT"
                  //       ? "red"
                  //       : props.dataItem.TransactionType === "CREDIT"
                  //       ? "green"
                  //       : "#000"
                  //     : "#000",
                  // }}
                >
                  {props.dataItem?.Balance
                    ? formatIndianNumber(props.dataItem?.Balance)
                    : ""}
                </td>
              ),
            }}
          />
        </KendoGrid>
      </div>
      {/* ) : null} */}
    </>
  );
};

export default AdminWalletTransactionView;
